"use client";

import React, { useEffect, useState } from "react";
import { ArrowRight, Quote } from "lucide-react";
import { Carousel, CarouselContent, CarouselItem, CarouselNext, CarouselPrevious, type CarouselApi } from "@/components/ui/carousel";
import { AspectRatio } from "@/components/ui/aspect-ratio";
import { Button } from "@/components/ui/button";
import { Link } from "@/components/common/Link";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
const projects = [{
  id: 1,
  title: "AI Strategy Implementation",
  metric: "40%",
  metricLabel: "Efficiency Gain",
  image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1577001/img/project_fintech_core.png",
  quote: "Cyril Consulting Group revolutionized our internal workflows with a bespoke AI strategy.",
  author: "David Chen, COO Global Logistics"
}, {
  id: 2,
  title: "Cloud Migration & Scaling",
  metric: "65%",
  metricLabel: "Cost Reduction",
  image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1577001/img/project_logistics_ai.png",
  quote: "The transition to a cloud-native architecture saved us millions in annual infrastructure costs.",
  author: "Sarah Jenkins, CTO Fintech Solutions"
}, {
  id: 3,
  title: "Automation Advisory",
  metric: "2.5x",
  metricLabel: "Output Increase",
  image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1577001/img/project_quantum_data.png",
  quote: "Our manufacturing output doubled within six months of implementing their automation roadmap.",
  author: "Marcus Thorne, VP of Operations"
}];
export default function PracticalOutcomes() {
  const {
    ref,
    isVisible
  } = useRevealOnScroll();
  const [api, setApi] = useState<CarouselApi>();
  const [current, setCurrent] = useState(0);
  useEffect(() => {
    if (!api) return;
    setCurrent(api.selectedScrollSnap());
    api.on("select", () => {
      setCurrent(api.selectedScrollSnap());
    });
  }, [api]);
  return <section data-section-id="32475" id="measurable-business-outcomes" ref={ref} className={`dark bg-[#1A2A4E] text-white py-24 relative overflow-hidden transition-opacity duration-1000 ${isVisible ? "opacity-100" : "opacity-0"}`}>
      {}
      <div className="absolute top-0 left-0 w-full h-full overflow-hidden pointer-events-none">
        <div className="absolute top-[-10%] right-[-5%] w-[500px] h-[500px] bg-[#008080]/10 rounded-full blur-3xl" />
        <div className="absolute bottom-[-10%] left-[-5%] w-[500px] h-[500px] bg-[#D4AF37]/5 rounded-full blur-3xl" />
      </div>

      <div className="container mx-auto px-4 relative z-10">
        <div className="mb-12 md:mb-16 max-w-2xl">
          <h2 className="text-3xl md:text-5xl font-sans font-bold tracking-tight mb-4 text-white">
            Measurable Business Outcomes
          </h2>
          <p className="text-slate-300 text-lg md:text-xl max-w-lg">At Cyril Consulting Group, every engagement is built around one question: what measurable outcome does this technology need to deliver? We help organizations of every size — from independent restaurants and growing SMBs to enterprise IT teams — implement AI strategies, modernize cloud infrastructure, and deploy automation solutions that reduce costs, improve operational efficiency, and create sustainable competitive advantage. We don't sell technology for technology's sake. We deliver results you can measure, defend, and build on.</p>
        </div>

        <Carousel setApi={setApi} opts={{
        loop: true,
        align: "center"
      }} className="w-full">
          <CarouselContent className="-ml-0">
            {projects.map((project, index) => <CarouselItem key={project.id} data-index={index} className="pl-0 basis-full">
                <div className="relative group overflow-hidden rounded-xl border border-white/10 bg-white/5 shadow-2xl">
                  <AspectRatio ratio={16 / 9}>
                    {}
                    <div className="absolute inset-0 overflow-hidden">
                      <img src={project.image} alt={project.title} className={`w-full h-full object-cover transition-transform duration-[1500ms] ease-out ${current === index ? "scale-100" : "scale-110"}`} />
                      {}
                      <div className="absolute inset-0 bg-[#1A2A4E]/70 backdrop-blur-[2px]" />
                    </div>

                    {}
                    <div className="absolute inset-0 flex flex-col items-center justify-center p-6 md:p-12 text-center">
                      {}
                      <div className="flex-1 flex flex-col items-center justify-center">
                        <span className="text-6xl md:text-8xl lg:text-9xl font-mono font-bold text-[#D4AF37] tracking-tighter drop-shadow-2xl bg-gradient-to-b from-[#D4AF37] to-[#D4AF37]/70 bg-clip-text text-transparent">
                          {project.metric}
                        </span>
                        <span className="text-[#008080] font-medium tracking-widest uppercase text-sm md:text-base mt-2 md:mt-4">
                          {project.metricLabel}
                        </span>
                      </div>

                      {}
                      <div className="mt-auto max-w-3xl mx-auto">
                        <Quote className="w-8 h-8 md:w-10 md:h-10 text-[#008080]/40 mx-auto mb-4" />
                        <blockquote className="text-xl md:text-2xl text-white/90 italic font-serif leading-relaxed">
                          &ldquo;{project.quote}&rdquo;
                        </blockquote>
                        <cite className="block mt-4 text-sm md:text-base text-slate-400 not-italic font-sans">
                          — {project.author}
                        </cite>
                      </div>
                    </div>
                  </AspectRatio>
                </div>
              </CarouselItem>)}
          </CarouselContent>

          {}
          <div className="flex justify-end gap-2 mt-6 md:mt-8 pr-2">
            <div className="relative flex gap-2">
              <CarouselPrevious className="static translate-y-0 translate-x-0 h-12 w-12 rounded-full border-white/10 bg-white/5 hover:bg-[#008080] hover:text-white hover:border-[#008080] transition-colors" />
              <CarouselNext className="static translate-y-0 translate-x-0 h-12 w-12 rounded-full border-white/10 bg-white/5 hover:bg-[#008080] hover:text-white hover:border-[#008080] transition-colors" />
            </div>
          </div>
        </Carousel>
      </div>
    </section>;
}